import { useCallback, useEffect, useState } from 'react';
import { Animated, findNodeHandle } from 'react-native';
import styled from 'styled-components/native';

import IconArrowRightCircle from '../../assets/svg/IconArrowRightCircle';
import { isWebPlatform } from '../../boot/utils';
import { DEVICE_TYPE, GET_LAYOUT_MAX_WIDTH } from '../../constants/layout';
import { DeviceType } from '../../types/Common';
import Touchable from '../Touchable';

interface Props {
  scrollRef;
  scrollOffset: Animated.Value;
  dataLength: number;
}

const DefaultIcon = IconArrowRightCircle;

export default ({ scrollRef, scrollOffset, dataLength }: Props) => {
  const getRefCurrent = useCallback(() => {
    if (scrollRef.current?.scrollToOffset) {
      return scrollRef.current;
    }

    return scrollRef.current?.listCurrent;
  }, []);

  const throttleScroll = useCallback((direction: 'left' | 'right') => {
    const refCurrent = getRefCurrent();
    const list: any = findNodeHandle(refCurrent);

    refCurrent.scrollToOffset?.({
      animated: true,
      offset:
        list.scrollLeft +
        GET_LAYOUT_MAX_WIDTH() * (direction === 'left' ? -1 : 1),
    });
  }, []);

  const [displayLeft, setDisplayLeft] = useState(false);
  const [displayRight, setDisplayRight] = useState(false);

  const calculateDisplay = useCallback(
    (offset: number, node?) => {
      const list: any = node ?? findNodeHandle(getRefCurrent());

      setDisplayLeft(offset > 0);
      setDisplayRight(offset < list?.scrollWidth - list?.clientWidth);
    },
    [getRefCurrent]
  );

  useEffect(() => {
    const list: any = findNodeHandle(getRefCurrent());
    const offset = list?.scrollLeft ?? 0;

    calculateDisplay(offset, list);
  }, [dataLength, calculateDisplay]);

  useEffect(() => {
    if (!scrollOffset) return;

    scrollOffset.addListener(e => {
      calculateDisplay(e.value);
    });

    return () => {
      scrollOffset.removeAllListeners();
    };
  }, [scrollOffset, calculateDisplay]);

  if (!isWebPlatform || DEVICE_TYPE !== DeviceType.DESKTOP) {
    return null;
  }

  return (
    <S.Box testID="box" pointerEvents="box-none">
      <Touchable
        testID="leftBtn"
        style={{
          transform: [{ rotate: '180deg' }],
          display: displayLeft ? 'flex' : 'none',
        }}
        accessibilityRole="button"
        onPress={() => throttleScroll('left')}
      >
        <S.DefaultIconWrapper>
          <DefaultIcon />
        </S.DefaultIconWrapper>
      </Touchable>
      <S.Blank />
      <Touchable
        testID="rightBtn"
        style={{ display: displayRight ? 'flex' : 'none' }}
        accessibilityRole="button"
        onPress={() => throttleScroll('right')}
      >
        <S.DefaultIconWrapper>
          <DefaultIcon />
        </S.DefaultIconWrapper>
      </Touchable>
    </S.Box>
  );
};

const S = {
  Box: styled.View`
    padding: 16px;
    width: 100%;
    height: 100%;
    position: absolute;
    align-items: center;
    flex-direction: row;
  `,
  Blank: styled.View`
    flex: 1;
  `,
  DefaultIconWrapper: styled.View`
    background-color: ${({ theme }) => theme.palette.white};
    border-radius: 100px;
  `,
};
